import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { removeLoadingKey } from '../helpers/common';

export const CustomReportsLoadingKeys = {
  list: 'CUSTOM_REPORTS_LIST',
  save: 'CUSTOM_REPORTS_SAVE',
  remove: 'CUSTOM_REPORTS_DELETE',
  assistant: 'CUSTOM_REPORTS_ASSISTANT',
};

const initialState = {
  reports: [],
  isLoaded: false,
  loadingKeys: [],
  error: null,
};

const rejectPayload = (error, fallback) => {
  if (error?.response?.data) {
    return error.response.data.error || error.response.data;
  }
  return fallback;
};

export const getCustomReports = createAsyncThunk(
  'customReports/list',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/custom-reports');
      return data;
    } catch (error) {
      return rejectWithValue(rejectPayload(error, 'Failed to load custom reports'));
    }
  }
);

export const saveCustomReport = createAsyncThunk(
  'customReports/save',
  async (definition, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/custom-reports', definition);
      return data;
    } catch (error) {
      return rejectWithValue(rejectPayload(error, 'Failed to save custom report'));
    }
  }
);

export const deleteCustomReport = createAsyncThunk(
  'customReports/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`/api/v1/custom-reports/${id}`);
      return { id };
    } catch (error) {
      return rejectWithValue(rejectPayload(error, 'Failed to delete custom report'));
    }
  }
);

export const askReportAssistant = createAsyncThunk(
  'customReports/assistant',
  async ({ messages, proposal }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/custom-reports/assistant', {
        messages,
        proposal,
      });
      return data;
    } catch (error) {
      return rejectWithValue(rejectPayload(error, 'Kip could not process this request'));
    }
  }
);

export const customReports = createSlice({
  name: 'customReports',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCustomReports.pending, (state) => {
        state.loadingKeys = [...state.loadingKeys, CustomReportsLoadingKeys.list];
        state.error = null;
      })
      .addCase(getCustomReports.fulfilled, (state, action) => {
        state.reports = action.payload?.reports || [];
        state.isLoaded = true;
        state.loadingKeys = removeLoadingKey(CustomReportsLoadingKeys.list, state.loadingKeys);
      })
      .addCase(getCustomReports.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || 'Failed to load custom reports';
        state.loadingKeys = removeLoadingKey(CustomReportsLoadingKeys.list, state.loadingKeys);
      })
      .addCase(saveCustomReport.pending, (state) => {
        state.loadingKeys = [...state.loadingKeys, CustomReportsLoadingKeys.save];
        state.error = null;
      })
      .addCase(saveCustomReport.fulfilled, (state, action) => {
        const report = action.payload?.report;
        if (report) {
          const index = state.reports.findIndex((r) => r.id === report.id);
          if (index === -1) {
            state.reports.push(report);
          } else {
            state.reports[index] = report;
          }
        }
        state.loadingKeys = removeLoadingKey(CustomReportsLoadingKeys.save, state.loadingKeys);
      })
      .addCase(saveCustomReport.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || 'Failed to save custom report';
        state.loadingKeys = removeLoadingKey(CustomReportsLoadingKeys.save, state.loadingKeys);
      })
      .addCase(deleteCustomReport.pending, (state) => {
        state.loadingKeys = [...state.loadingKeys, CustomReportsLoadingKeys.remove];
        state.error = null;
      })
      .addCase(deleteCustomReport.fulfilled, (state, action) => {
        state.reports = state.reports.filter((r) => r.id !== action.payload.id);
        state.loadingKeys = removeLoadingKey(CustomReportsLoadingKeys.remove, state.loadingKeys);
      })
      .addCase(deleteCustomReport.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || 'Failed to delete custom report';
        state.loadingKeys = removeLoadingKey(CustomReportsLoadingKeys.remove, state.loadingKeys);
      })
      .addCase(askReportAssistant.pending, (state) => {
        state.loadingKeys = [...state.loadingKeys, CustomReportsLoadingKeys.assistant];
        state.error = null;
      })
      .addCase(askReportAssistant.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(CustomReportsLoadingKeys.assistant, state.loadingKeys);
      })
      .addCase(askReportAssistant.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || 'Kip could not process this request';
        state.loadingKeys = removeLoadingKey(CustomReportsLoadingKeys.assistant, state.loadingKeys);
      });
  },
});

export default customReports.reducer;
